<template>
<div class="fassung-waehlen">
    <ZugStepper :anzahl="uebung.zuege.length" :aktuell="erledigt.length" />
    <AblaufKopf :uebung="uebung" />


    <!-- Fertige Zuege, wie in "Alles sortieren" -->
    <div v-for="z in erledigt" :key="z.nr" class="erledigt" :style="einzug(z)">
        <p class="station nur-vorlesen">{{ z.station.rufname }}</p>
        <p class="spruch">{{ textVon(z.teile) }}</p>
    </div>

    <div v-if="!geloest" :key="zug.nr">
        <p v-if="uebung.zuege.length > 1" class="station" :style="{ marginLeft: einzug(zug).marginLeft }">{{ zug.station.rufname }}</p>
        <p class="auftrag">Welche Fassung ist richtig? <span class="tastenhinweis">Klick oder Taste {{ 'ABCDEFGH'.slice(0, fassungen.length).split('').join(', ') }}</span></p>

        <div class="fassungen">
            <div v-for="(f, i) in fassungen" :key="f.text" class="fassung">
                <button
                    :class="{ daneben: gewaehlt.includes(f) }"
                    :disabled="gewaehlt.includes(f)"
                    :aria-keyshortcuts="'ABCDEFGH'[i]"
                    @click="waehlen(f)">
                    <kbd aria-hidden="true">{{ 'ABCDEFGH'[i] }}</kbd>
                    <span class="fassung__text">{{ f.text }}</span>
                </button>
                <ul v-if="gewaehlt.includes(f)" class="regeln">
                    <li v-for="r in f.regeln" :key="r">{{ r }}</li>
                </ul>
            </div>
        </div>
    </div>

    <p v-if="geloest" class="geloest">Das sitzt.</p>

    <div class="knopfleiste">
        <slot name="rechts" />
    </div>

</div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import AblaufKopf from '@/components/AblaufKopf.vue';
import ZugStepper from '@/components/ZugStepper.vue';
import { einzugFuer } from '@/logic/Einzug';

// Uebungsart "Richtige Fassung wählen": Zug fuer Zug stehen drei Fassungen
// eines Spruchs da, nur eine ist richtig. Die beiden falschen haben je einen
// Fehler (Ersatz oder Tausch). Eine falsche Wahl zeigt ihre Regel und bleibt
// gesperrt; die richtige setzt den Zug fest, dann kommt der naechste.
//
// Vertrag mit dem Container wie bei jeder Uebungsart (siehe AllesSortieren).
// Jede Wahl ist ein Pruefen. Die Fassungen sind schon gewuerfelt:
// uebung.fehlersuche, siehe bereiteVor().

export default {
    components: { AblaufKopf, ZugStepper },
    // Der Container reicht allen Ansichten dieselben Props; was diese nicht
    // braucht, soll nicht als HTML-Attribut am Wurzelelement landen.
    inheritAttrs: false,
    props: {
        uebung: {type: Object, required: true}
    },
    emits: ['geprueft'],
    setup(props, { emit }) {
        const textVon = (teile) => teile.map(t => t.text).join(' ')
        const einzug = einzugFuer(props.uebung)

        const fassungenProZug = props.uebung.fehlersuche.fassungen

        const aktuell = ref(0)
        const zug = computed(() => props.uebung.zuege[aktuell.value])
        const erledigt = computed(() => props.uebung.zuege.slice(0, aktuell.value))
        const fassungen = computed(() => fassungenProZug[aktuell.value])

        const gewaehlt = ref([])
        const geloest = ref(false)

        const waehlen = (f) => {
            const letzter = aktuell.value === props.uebung.zuege.length - 1
            emit('geprueft', { sitzt: f.richtig, geloest: f.richtig && letzter })

            if (!f.richtig) {
                gewaehlt.value.push(f)
                return
            }
            gewaehlt.value = []
            // Auch der letzte Zug wandert zu den erledigten. Die Auswahl
            // bleibt dann leer stehen — darum zeigt sie v-if nicht mehr.
            if (letzter) geloest.value = true
            aktuell.value += 1
        }


        // Tastenkuerzel: A, B, C … waehlen die Moeglichkeit mit diesem
        // Buchstaben. Nicht mit Strg/Alt/Cmd und nicht in Eingabefeldern.
        const BUCHSTABEN = 'abcdefgh'
        const taste = (e) => {
            if (e.ctrlKey || e.altKey || e.metaKey) return
            if (['INPUT', 'SELECT', 'TEXTAREA'].includes(e.target.tagName)) return
            const i = BUCHSTABEN.indexOf(e.key.toLowerCase())
            if (i === -1) return
            const f = fassungen.value?.[i]
            if (geloest.value || !f || gewaehlt.value.includes(f)) return
            waehlen(f)
        }
        onMounted(() => window.addEventListener('keydown', taste))
        onUnmounted(() => window.removeEventListener('keydown', taste))

        return { textVon, einzug, zug, erledigt, fassungen, gewaehlt, geloest, waehlen }
    }
}
</script>

<style scoped>
/* Station = Farbe Blau, wie ueberall: Farbe steht fuer Station oder fuer
   richtig/falsch, fuer nichts sonst. */
.station {
    margin: 0 0 0.35rem;
    font-size: 0.8rem;
    font-weight: 650;
    color: var(--zs-blau);
}

/* Fertige Zuege: stehen fest, olivgruen = richtig. */
.erledigt {
    margin: 0 0 1rem;
    padding: 0.7rem 0.9rem;
    background: var(--zs-oliv-hell);
    border: 1px solid rgba(85, 107, 47, 0.4);
    border-radius: var(--zs-ecke);
}

.erledigt .spruch {
    margin: 0;
    line-height: 1.45;
}

.auftrag {
    margin: 0 0 1.25rem;
    font-weight: 600;
    color: var(--zs-tinte);
}

.fassungen {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.fassung button {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    width: 100%;
    min-height: 3rem;
    padding: 0.5rem 0.85rem;
    font: inherit;
    line-height: 1.4;
    color: var(--zs-tinte);
    background: var(--zs-tafel);
    border: 1px solid var(--zs-rahmen);
    border-radius: var(--zs-ecke);
    cursor: pointer;
    transition: border-color 0.15s, background-color 0.15s;
    text-align: left;
}

.fassung button:hover:not(:disabled) {
    border-color: var(--zs-tinte);
}

/* Falsch gewaehlt: rot hinterlegt UND mit dem Wort «falsch» am Ende. */
.fassung button.daneben {
    cursor: default;
    background: var(--zs-warnrot-hell);
    border-color: rgba(179, 38, 30, 0.45);
}

.fassung button.daneben::after {
    content: '\2715\00a0 falsch';
    flex: none;
    margin-left: auto;
    font-family: 'Archivo', system-ui, sans-serif;
    font-size: 0.85rem;
    font-weight: 650;
    color: var(--zs-warnrot);
}

/* Die Taste zur Moeglichkeit, wie eine Taste gezeichnet. */
kbd {
    display: inline-grid;
    place-items: center;
    flex: none;
    min-width: 1.6rem;
    height: 1.6rem;
    font-family: var(--zs-funkschrift);
    font-size: 0.8rem;
    color: var(--zs-grau);
    background: var(--zs-flaeche);
    border: 1px solid var(--zs-rahmen);
    border-bottom-width: 2px;
    border-radius: 3px;
}

.tastenhinweis {
    margin-left: 0.5rem;
    font-size: 0.85rem;
    font-weight: 400;
    color: var(--zs-grau);
}

.fassung button.daneben kbd {
    color: var(--zs-warnrot);
    border-color: rgba(179, 38, 30, 0.45);
}

.regeln {
    margin: 0.35rem 0 0.25rem;
    padding-left: 1.6rem;
    font-size: 0.95rem;
    line-height: 1.45;
    color: var(--zs-warnrot);
}

.geloest {
    margin: 0;
    padding: 0.85rem 1rem;
    font-weight: 650;
    color: var(--zs-oliv);
    background: var(--zs-oliv-hell);
    border: 1px solid rgba(85, 107, 47, 0.45);
    border-radius: var(--zs-ecke);
}

.geloest::before {
    content: '\2713\00a0\00a0';
}

/* ─── Knopfleiste ────────────────────────────────────────────
   Rechts kommt, was dem Container gehoert (Neue Uebung / Weiter). */

.knopfleiste {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem;
    margin-top: 2rem;
    padding-top: 1rem;
    border-top: 1px solid var(--zs-rahmen);
}

button:focus-visible {
    outline: 3px solid var(--zs-orange);
    outline-offset: 2px;
}

/* Funktext in fester Laufweite: Protokolltext, nicht Bedientext. */
.erledigt .spruch,
.fassung button {
    font-family: var(--zs-funkschrift);
}

@media (prefers-reduced-motion: reduce) {
    .fassung button {
        transition: none;
    }
}
</style>
